// Step 3 of writing a recipe (wireframe 20): one box per step, numbered,
// with sections, notes, moving and "Paste all steps". Also the pieces the
// ingredient list shares: moving a row and a section's heading.
import { useState } from "react";
import { COLORS, FONT_SIZE, LAYOUT, SPACE } from "../../lib/constants";
import { buildGroupedRows } from "../../lib/recipeUtils";
import { moveRow, stepsFromText, type EntryStep } from "../../lib/recipeEntry";
import { uid } from "../../lib/utils";
import { smallLabel, supporting } from "../recipeStyles";
import { textButton } from "../plan/planStyles";
import { Field } from "./WriteParts";
import { fieldInput, primaryButton, secondaryButton, stepHeading } from "./writeStyles";

type Row = { kind: "header"; key: string; label: string; itemId: string } | { kind: "item"; key: string; item: EntryStep; firstOfRun: boolean };

export function StepsStep({ steps, onChange }: { steps: EntryStep[]; onChange: (steps: EntryStep[]) => void }) {
  const [pasting, setPasting] = useState(false);
  const [pasted, setPasted] = useState("");
  const [naming, setNaming] = useState(false);
  const [newSection, setNewSection] = useState("");
  const [focusId, setFocusId] = useState<string | null>(null);
  const section = steps[steps.length - 1]?.component ?? "";

  const append = (type: "step" | "note", component = section) => {
    const step: EntryStep = { id: uid(), component, type, text: "" };
    onChange([...steps, step]);
    setFocusId(step.id);
  };
  const update = (id: string, text: string) => onChange(steps.map((s) => (s.id === id ? { ...s, text } : s)));
  const remove = (id: string) => onChange(steps.filter((s) => s.id !== id));
  const renameSection = (from: string, to: string) => onChange(steps.map((s) => (s.component === from ? { ...s, component: to } : s)));

  let number = 0;
  const rows = buildGroupedRows(steps) as Row[];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: SPACE.xl }}>
      <h1 style={stepHeading}>Steps</h1>
      <div style={{ display: "flex", flexDirection: "column" }}>
        {rows.map((row) => {
          if (row.kind === "header") return <SectionHeader key={row.key} label={row.label} onRename={(to) => renameSection(row.label, to)} />;
          const s = row.item;
          const isNote = s.type === "note";
          if (!isNote) number += 1;
          return (
            <div key={row.key} style={{ display: "flex", gap: SPACE.md, alignItems: "flex-start", padding: "var(--space-md) 0", borderBottom: `1px solid ${COLORS.line}`, paddingLeft: s.component ? "var(--space-xl)" : 0 }}>
              <span aria-hidden="true" style={{ width: 28, flexShrink: 0, paddingTop: SPACE.md, fontWeight: 700, color: isNote ? COLORS.soft : COLORS.accent, fontSize: FONT_SIZE.base }}>
                {isNote ? "✎" : number}
              </span>
              <textarea
                aria-label={isNote ? "Note" : `Step ${number}`}
                autoFocus={focusId === s.id}
                rows={2}
                value={s.text}
                placeholder={isNote ? "A note between steps" : "One action per step"}
                onChange={(e) => update(s.id, e.target.value)}
                style={{ ...fieldInput, flex: 1, padding: "var(--space-md) var(--space-lg)", fontFamily: "var(--font-sans)", fontStyle: isNote ? "italic" : "normal", resize: "vertical" }}
              />
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <RowMover items={steps} id={s.id} onMove={(next) => onChange(next as EntryStep[])} label={isNote ? "note" : `step ${number}`} />
                <button type="button" aria-label={`Delete ${isNote ? "note" : `step ${number}`}`} onClick={() => remove(s.id)} style={{ width: LAYOUT.tapTarget, height: 36, border: "none", background: "transparent", color: COLORS.danger, fontSize: FONT_SIZE.h4, cursor: "pointer" }}>
                  ×
                </button>
              </div>
            </div>
          );
        })}
        {steps.length === 0 && <p style={{ ...supporting, margin: 0 }}>No steps yet.</p>}
      </div>

      {naming ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (newSection.trim()) append("step", newSection.trim());
            setNewSection("");
            setNaming(false);
          }}
          style={{ display: "flex", gap: SPACE.md, alignItems: "flex-end" }}
        >
          <Field label="New section name">
            <input autoFocus value={newSection} placeholder="For the sauce" onChange={(e) => setNewSection(e.target.value)} style={fieldInput} />
          </Field>
          <button type="submit" style={secondaryButton}>
            Start section
          </button>
        </form>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: SPACE.xl }}>
          <button type="button" onClick={() => append("step")} style={textButton}>
            + Step
          </button>
          <button type="button" onClick={() => setNaming(true)} style={textButton}>
            + Section
          </button>
          <button type="button" onClick={() => append("note")} style={textButton}>
            + Note
          </button>
          <button type="button" aria-expanded={pasting} onClick={() => setPasting((p) => !p)} style={textButton}>
            Paste all steps
          </button>
        </div>
      )}

      {pasting && (
        <div style={{ display: "flex", flexDirection: "column", gap: SPACE.md }}>
          <Field label="One step per line, or per paragraph" hint="Numbers like “1.” are dropped. A line like “For the sauce:” starts a section.">
            <textarea autoFocus rows={8} value={pasted} onChange={(e) => setPasted(e.target.value)} style={{ ...fieldInput, padding: "var(--space-md) var(--space-lg)", fontFamily: "var(--font-sans)" }} />
          </Field>
          <button
            type="button"
            disabled={!pasted.trim()}
            onClick={() => {
              onChange([...steps.filter((s) => s.text.trim()), ...stepsFromText(pasted, section)]);
              setPasted("");
              setPasting(false);
            }}
            style={{ ...primaryButton, alignSelf: "flex-start", opacity: pasted.trim() ? 1 : 0.6 }}
          >
            Add these
          </button>
        </div>
      )}
      <p style={{ ...supporting, margin: 0 }}>One action per step reads best in the cooking view, where each step is shown large and ticked off in turn.</p>
    </div>
  );
}

// ↑ and ↓ for one row (moveRow in recipeEntry.ts).
export function RowMover({ items, id, onMove, label }: { items: { id: string; component: string }[]; id: string; onMove: (items: { id: string; component: string }[]) => void; label: string }) {
  const i = items.findIndex((x) => x.id === id);
  const arrow = { width: LAYOUT.tapTarget, height: 32, border: "none", background: "transparent", color: COLORS.soft, fontSize: FONT_SIZE.base, cursor: "pointer" } as const;
  return (
    <span style={{ display: "inline-flex", flexDirection: "column", flexShrink: 0 }}>
      <button type="button" aria-label={`Move ${label} up`} disabled={i <= 0} onClick={() => onMove(moveRow(items, id, -1))} style={{ ...arrow, opacity: i <= 0 ? 0.3 : 1 }}>
        ↑
      </button>
      <button type="button" aria-label={`Move ${label} down`} disabled={i === items.length - 1} onClick={() => onMove(moveRow(items, id, 1))} style={{ ...arrow, opacity: i === items.length - 1 ? 0.3 : 1 }}>
        ↓
      </button>
    </span>
  );
}

// A section's heading with Rename.
export function SectionHeader({ label, onRename }: { label: string; onRename: (to: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(label);
  if (editing) {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (value.trim()) onRename(value.trim());
          setEditing(false);
        }}
        style={{ display: "flex", gap: SPACE.md, alignItems: "center", padding: "var(--space-md) 0" }}
      >
        <input aria-label="Section name" autoFocus value={value} onChange={(e) => setValue(e.target.value)} style={{ ...fieldInput, flex: 1 }} />
        <button type="submit" style={secondaryButton}>
          Rename
        </button>
      </form>
    );
  }
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: SPACE.md, paddingTop: SPACE.md }}>
      <span style={{ ...smallLabel, color: COLORS.accent }}>Section: {label}</span>
      <button type="button" onClick={() => (setValue(label), setEditing(true))} style={{ ...textButton, fontSize: FONT_SIZE.sm }}>
        Rename
      </button>
    </div>
  );
}
